import { useState } from 'react';
import { Badge, Box, Button, Flex, Separator, Text } from '@optiaxiom/react';
import { IconAngleDown, IconAngleRight, IconLink, IconLinkSlash } from '@optimizely/axiom-icons';

import { ProviderConnectionSummary } from '@frontend/types';

type ProviderConnectionCardProps = {
  provider: ProviderConnectionSummary;
  onConnect: (providerId: string) => void;
  onDisconnect: (credentialId: string) => void;
  isConnecting: boolean;
};

const getStatusBadge = (status: ProviderConnectionSummary['connectionStatus']) => {
  switch (status) {
    case 'connected':
      return <Badge intent="success">Connected</Badge>;
    case 'expired':
      return <Badge intent="warning">Expired</Badge>;
    case 'not_connected':
    default:
      return <Badge intent="neutral">Not Connected</Badge>;
  }
};

export function ProviderConnectionCard({
  provider,
  onConnect,
  onDisconnect,
  isConnecting,
}: ProviderConnectionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleConnect = () => {
    onConnect(provider.providerId);
  };

  const handleDisconnect = () => {
    if (provider.connection?.id) {
      onDisconnect(provider.connection.id);
    }
  };

  return (
    <Box border="1" borderColor="border.secondary" rounded="md" bg="bg.default">
      <Flex p="16" justifyContent="space-between" alignItems="center">
        <Flex gap="12" alignItems="center">
          <Button
            appearance="subtle"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <IconAngleDown size={16} /> : <IconAngleRight size={16} />}
          </Button>
          <Flex flexDirection="column" gap="2">
            <Flex gap="8" alignItems="center">
              <Text fontWeight="500">{provider.providerName}</Text>
              {getStatusBadge(provider.connectionStatus)}
            </Flex>
            {provider.providerDescription && (
              <Text color="fg.tertiary" fontSize="sm">
                {provider.providerDescription}
              </Text>
            )}
          </Flex>
        </Flex>

        <Flex gap="8">
          {provider.connectionStatus === 'not_connected' && (
            <Button appearance="primary" size="sm" onClick={handleConnect} disabled={isConnecting}>
              <IconLink size={16} />
              {isConnecting ? 'Connecting...' : 'Connect'}
            </Button>
          )}
          {provider.connectionStatus === 'expired' && (
            <Button appearance="primary" size="sm" onClick={handleConnect} disabled={isConnecting}>
              <IconLink size={16} />
              {isConnecting ? 'Reconnecting...' : 'Reconnect'}
            </Button>
          )}
          {provider.connectionStatus === 'connected' && provider.connection?.id && (
            <Button appearance="subtle" size="sm" onClick={handleDisconnect}>
              <IconLinkSlash size={16} />
              Disconnect
            </Button>
          )}
        </Flex>
      </Flex>

      {isExpanded && provider.toolsUsingProvider.length > 0 && (
        <>
          <Separator />
          <Box p="16" bg="bg.secondary">
            <Text fontSize="sm" fontWeight="500" mb="8">
              Tools using this provider:
            </Text>
            <Flex flexDirection="column" gap="4">
              {provider.toolsUsingProvider.map(toolName => (
                <Text key={toolName} fontSize="sm" color="fg.secondary">
                  {toolName}
                </Text>
              ))}
            </Flex>
          </Box>
        </>
      )}
    </Box>
  );
}
